

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SplashScreen.css';

/**
 * SplashScreen Component
 * 
 * Displays an animated splash screen on app launch with:
 * - 3-second zoom-out animation from logo center
 * - Static noise effect on orange screen (first 0.75s)
 * - 3D glossy frame effect on screen bezel
 * - Click-anywhere to skip to login
 * - Automatic navigation to login after animation
 * 
 * @component
 */
const SplashScreen = ({ onComplete }) => {
  const navigate = useNavigate();
  const [showNoise, setShowNoise] = useState(true);
  const [animationComplete, setAnimationComplete] = useState(false);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const noiseTimeoutRef = useRef(null);
  const navigationTimeoutRef = useRef(null);

  /**
   * Generates animated static noise on canvas element
   * Creates a grainy TV static effect
   */
  useEffect(() => {
    if (!showNoise || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size to match display size
    const updateCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      // Only update if we have valid dimensions
      if (rect.width > 0 && rect.height > 0) {
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
    };
    
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    /**
     * Draws random static noise pixels on canvas
     * Creates the classic TV static effect
     */
    const drawNoise = () => {
      // Safety check: ensure canvas has valid dimensions
      if (canvas.width === 0 || canvas.height === 0 || !canvas.width || !canvas.height) {
        // Retry after a short delay if dimensions aren't ready
        animationFrameRef.current = requestAnimationFrame(drawNoise);
        return;
      }

      const imageData = ctx.createImageData(canvas.width, canvas.height);
      const buffer = new Uint32Array(imageData.data.buffer);

      // Fill with random grayscale pixels
      for (let i = 0; i < buffer.length; i++) {
        // Generate random grayscale value
        const value = Math.random() * 255;
        // Create RGBA pixel (alpha = 255 for full opacity)
        buffer[i] = (255 << 24) | (value << 16) | (value << 8) | value;
      }

      ctx.putImageData(imageData, 0, 0);
      
      // Continue animation loop
      if (showNoise) {
        animationFrameRef.current = requestAnimationFrame(drawNoise);
      }
    };

    // Start noise animation with a small delay to ensure canvas is ready
    const startAnimationFrame = requestAnimationFrame(() => {
      updateCanvasSize(); // One more update to be sure
      drawNoise(); // Start noise animation
    });

    // Cleanup function
    return () => {
      if (startAnimationFrame) {
        cancelAnimationFrame(startAnimationFrame);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [showNoise]);

  /**
   * Manages animation timing and automatic navigation
   */
  useEffect(() => {
    // Stop showing noise after 750ms (1/4 of 3 seconds)
    noiseTimeoutRef.current = setTimeout(() => {
      setShowNoise(false);
    }, 750);

    // Mark animation as complete and navigate after 3 seconds
    navigationTimeoutRef.current = setTimeout(() => {
      setAnimationComplete(true);
      onComplete(); // Update parent state first
      navigate('/login'); // Then navigate
    }, 3000);

    // Cleanup timeouts on unmount
    return () => {
      if (noiseTimeoutRef.current) {
        clearTimeout(noiseTimeoutRef.current);
      }
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [navigate]);

  /**
   * Handles click event to skip animation
   * Navigates immediately to login page
   */
  const handleClick = () => {
    // Clear pending timeouts to prevent double navigation
    if (noiseTimeoutRef.current) {
      clearTimeout(noiseTimeoutRef.current);
    }
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Update parent state first, then navigate
    onComplete();
    navigate('/login');
  };

  return (
    <div 
      className="splash-screen-container" 
      onClick={handleClick}
      role="button"
      aria-label="Click to skip splash screen"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      {/* Logo container with zoom-out animation */}
      <div className="splash-logo-wrapper">
        {/* Computer monitor icon with 3D glossy frame */}
        <div className="monitor-frame">
          {/* Glossy 3D bezel effect */}
          <div className="bezel bezel-top"></div>
          <div className="bezel bezel-right"></div>
          <div className="bezel bezel-bottom"></div>
          <div className="bezel bezel-left"></div>
          
          {/* Monitor screen with orange background */}
          <div className="monitor-screen">
            {/* Static noise overlay (only shown first 750ms) */}
            {showNoise && (
              <canvas 
                ref={canvasRef} 
                className="static-noise-canvas"
                aria-hidden="true"
              />
            )}
          </div>
          
          {/* Monitor stand */}
          <div className="monitor-stand">
            <div className="stand-neck"></div>
            <div className="stand-base"></div>
          </div>
        </div>

        {/* "n Screen" text */}
        <div className="logo-text">
          <span className="logo-text-n">n</span>
          <span className="logo-text-screen"> Screen</span>
        </div>
      </div>

      {/* Skip hint (fades in after 1 second) */}
      <div className="skip-hint">
        Click anywhere to continue
      </div>
    </div>
  );
};

export default SplashScreen;